import { useState, useCallback } from 'react';
import {
  readLayoutFromStorage,
  loadLayoutFromApi,
} from '../../services/homeLauncher/launcherPersistenceService';
import { save as legacySave } from '../../services/homeLauncher/adapters/legacyLauncherAdapter';
import { save as workspaceSave } from '../../services/homeLauncher/adapters/workspaceAdapter';
import { RUNTIME_SOURCE, legacyRuntimeContext } from '../../services/workspaceRuntime/workspaceResolverService';

/**
 * Owns the load/save lifecycle for the launcher layout.
 *
 * Phase 3B additions:
 *  - runtimeContext: tracks { runtimeSource, workspaceId, workspaceVersionId, … }
 *  - saveLayout now routes to the correct persistence adapter based on runtimeSource
 *
 * Load priority (legacy path only):
 *   1. localStorage cache  → source: 'cache'
 *   2. API GET             → source: 'api'
 *   3. Error fallback      → source: 'error', data: null
 *
 * Write routing:
 *   runtimeSource === 'legacy'    → legacyLauncherAdapter.save (localStorage)
 *   runtimeSource === 'workspace' → workspaceAdapter.save (boundary-only, Phase 3C)
 */
/**
 * @param {{ initialRuntimeContext?: import('../services/workspaceRuntime/workspaceResolverService').RuntimeContext }} [options]
 */
export default function useLauncherPersistence({ initialRuntimeContext } = {}) {
  const [layoutLoaded,    setLayoutLoaded]    = useState(false);
  const [runtimeContext,  setRuntimeContext]  = useState(initialRuntimeContext ?? legacyRuntimeContext);

  /**
   * Load the layout for a given user uid via the LEGACY path.
   * Only called when workspace resolution returns useLegacy=true.
   * Returns { data, source }.
   */
  const loadLayout = useCallback(async (uid) => {
    const cached = readLayoutFromStorage(uid);
    if (cached) return { data: cached, source: 'cache' };
    try {
      const data = await loadLayoutFromApi();
      return { data, source: 'api' };
    } catch {
      return { data: null, source: 'error' };
    }
  }, []);

  /**
   * Persist a layout snapshot using the correct adapter for the current runtimeSource.
   *
   * Overloads:
   *   saveLayout(uid, fullObject)          – { icons, theme, gridColumns, … }
   *   saveLayout(uid, iconsArray, config)  – convenience split
   */
  const saveLayout = useCallback((uid, iconsOrFull, config) => {
    if (!uid) return;

    const snapshot = config
      ? { icons: iconsOrFull, ...config }
      : iconsOrFull;

    if (runtimeContext.runtimeSource === RUNTIME_SOURCE.WORKSPACE) {
      workspaceSave(runtimeContext, snapshot);
      return;
    }

    legacySave(uid, snapshot);
  }, [runtimeContext]);

  return {
    layoutLoaded,
    setLayoutLoaded,
    runtimeContext,
    setRuntimeContext,
    loadLayout,
    saveLayout,
  };
}
